import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { BaseMatcherEditorComponent, MatcherComponentRegistryService } from '@cdw-ae/filter-bar-core';

import { FilterBarDateTimeMatcherOptions, FieldMatcher } from '@cdw-ae/filter-bar-interfaces';
import { map } from 'rxjs';

type DateTimeMatcherOptionsKeys = keyof typeof FilterBarDateTimeMatcherOptions;

type FormStateType = Partial<{
  selectedOption: DateTimeMatcherOptionsKeys | null;
  startDateTime: Date | null;
  endDateTime: Date | null;
}>;

type MatcherValueType = { option: DateTimeMatcherOptionsKeys; value: string | { start: string; end: string } };

export const DateTimeMatcher: FieldMatcher = {
  name: 'dateTime',
  displayName: 'Match by DateTime',
  hasEditor: true,
  options: {
    toolTipFormatter: (value: string): string => {
      const optionValue: MatcherValueType = JSON.parse(value) as MatcherValueType;

      if (optionValue.option === 'beforeDateTime' || optionValue.option === 'sinceDateTime') {
        const formatDate = Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'medium' }).format(
          new Date(optionValue.value as string)
        );
        return `${FilterBarDateTimeMatcherOptions[optionValue.option]} ${formatDate}`;
      }

      if (optionValue.option === 'betweenDateTime') {
        const range = optionValue.value as { start: string; end: string };
        const startDate = Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'medium' }).format(
          new Date(range.start)
        );
        const endDate = Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'medium' }).format(
          new Date(range.end)
        );
        return `${FilterBarDateTimeMatcherOptions[optionValue.option]} ${startDate} and ${endDate}`;
      }

      return '';
    },

    chipFormatter: (value: string): string => {
      const optionValue: MatcherValueType = JSON.parse(value) as MatcherValueType;
      if (optionValue.option === 'beforeDateTime' || optionValue.option === 'sinceDateTime') {
        const formatDate = Intl.DateTimeFormat('en-US', { dateStyle: 'short', timeStyle: 'short' }).format(
          new Date(optionValue.value as string)
        );
        return `: ${FilterBarDateTimeMatcherOptions[optionValue.option]} ${formatDate}`;
      }

      if (optionValue.option === 'betweenDateTime') {
        const range = optionValue.value as { start: string; end: string };
        const startDate = Intl.DateTimeFormat('en-US', { dateStyle: 'short' }).format(new Date(range.start));

        const endDate = Intl.DateTimeFormat('en-US', { dateStyle: 'short' }).format(new Date(range.end));

        return `: ${FilterBarDateTimeMatcherOptions[optionValue.option]} ${startDate} and ${endDate}`;
      }

      return FilterBarDateTimeMatcherOptions[optionValue.option].toLocaleLowerCase();
    },
  },
  editorOptions: {
    selectionOptions: [],
  },
};

@Component({
  selector: 'cdw-date-time-matcher',
  template: `
    <div class="flex flex-col gap-y-0">
      <mat-form-field [formGroup]="state">
        <mat-label>Select a date range</mat-label>
        <mat-select formControlName="selectedOption" cdkFocusInitial>
          <mat-option *ngFor="let dtOption of DateTimeMatcherOptions" [value]="dtOption[0]">{{
            dtOption[1]
          }}</mat-option>
        </mat-select>
      </mat-form-field>

      <mat-form-field
        *ngIf="
          state.controls.selectedOption.value === 'sinceDateTime' ||
          state.controls.selectedOption.value === 'beforeDateTime'
        "
        [formGroup]="state"
      >
        <mat-label>Choose a date</mat-label>
        <input matInput [matDatepicker]="picker" formControlName="startDateTime" />
        <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
        <mat-datepicker #picker></mat-datepicker>
        <mat-error *ngIf="state.controls.startDateTime.hasError('matDatepickerParse')" class="h-2-rem"
          >Invalid date format
        </mat-error>
      </mat-form-field>

      <div class="flex flex-col gap-y-4" *ngIf="state.controls.selectedOption.value === 'betweenDateTime'">
        <mat-form-field [formGroup]="state">
          <mat-label>Start date</mat-label>
          <input
            matInput
            [matDatepicker]="pickerStart"
            formControlName="startDateTime"
          />
          <mat-datepicker-toggle matSuffix [for]="pickerStart"></mat-datepicker-toggle>
          <mat-datepicker #pickerStart></mat-datepicker>
          <mat-error *ngIf="state.controls.startDateTime.hasError('matDatepickerParse')" class="h-2-rem"
            >Invalid date format
          </mat-error>
          <mat-error *ngIf="state.controls.startDateTime.hasError('matStartDateInvalid')" class="h-2-rem">
            Start date must be before end date
          </mat-error>
        </mat-form-field>

        <mat-form-field [formGroup]="state">
          <mat-label>End date</mat-label>
          <input
            matInput
            [matDatepicker]="pickerEnd"
            formControlName="endDateTime"
          />
          <mat-datepicker-toggle matSuffix [for]="pickerEnd"></mat-datepicker-toggle>
          <mat-datepicker #pickerEnd></mat-datepicker>
          <mat-error *ngIf="state.controls.endDateTime.hasError('matDatepickerParse')" class="h-2-rem"
            >Invalid date format
          </mat-error>
          <mat-error *ngIf="state.controls.endDateTime.hasError('matEndDateInvalid')" class="h-2-rem">
            End date must be after start date
          </mat-error>
        </mat-form-field>
      </div>
    </div>
  `,
  styles: [
    `
      mat-form-field {
        width: 100%;
      }
    `,
  ],
  standalone: false,
})
export class DateTimeMatcherComponent extends BaseMatcherEditorComponent implements OnInit {
  static registerDateTime(registry: MatcherComponentRegistryService): void {
    registry.registerComponent('dateTime', DateTimeMatcherComponent);
  }
  public DateTimeMatcherOptions = Object.entries(FilterBarDateTimeMatcherOptions);

  public override state = new FormGroup({
    selectedOption: new FormControl<DateTimeMatcherOptionsKeys | null>(null),
    startDateTime: new FormControl<Date | null>(null),
    endDateTime: new FormControl<Date | null>(null),
  });

  constructor() {
    super();
  }

  public ngOnInit(): void {
    this.matcherDisplayName = DateTimeMatcher.displayName;
    this.matcherName = DateTimeMatcher.name;

    this.hasErrors$ = this.state.valueChanges.pipe(
      map(
        (state: FormStateType): boolean => {
          if (
            (state.selectedOption === 'beforeDateTime' || state.selectedOption === 'sinceDateTime') &&
            (this.state.controls.startDateTime.hasError('matDatepickerParse') || state.startDateTime === null)
          ) {
            return true;
          }
          if (
            state.selectedOption === 'betweenDateTime' &&
            (this.state.controls.startDateTime.hasError('matDatepickerParse') ||
              state.startDateTime === null ||
              this.state.controls.endDateTime.hasError('matDatepickerParse') ||
              state.endDateTime === null)
          ) {
            return true;
          }
          return false;
        }
      )
    );

    // biome-ignore lint/suspicious/useAwait: <Suppressed for migration>
    this.state.valueChanges.subscribe(async (state: FormStateType): Promise<void> => {
      this.state.setErrors(null);
      this.state.controls.startDateTime.setErrors(null);
      this.state.controls.endDateTime.setErrors(null);

      if (!state.selectedOption) {
        this.valueToMatch = null;
        return;
      }

      if (state.selectedOption === 'beforeDateTime' || state.selectedOption === 'sinceDateTime') {
        this.valueToMatch = this.beforeAfterHandler(state);
        return;
      }

      if (state.selectedOption === 'betweenDateTime') {
        this.valueToMatch = this.betweenHandler(state);
        return;
      }
      this.valueToMatch = JSON.stringify({ option: state.selectedOption, value: true });
    });
  }

  private beforeAfterHandler(state: FormStateType): string | null {
    if (this.state.controls.startDateTime.errors) {
      return null;
    }

    if (!state.startDateTime) {
      this.state.controls.startDateTime.setErrors({ matDatepickerParse: true });
      return null;
    }

    return JSON.stringify({
      option: state.selectedOption,
      value: state.startDateTime.toISOString(),
    });
  }

  private betweenHandler(state: FormStateType): string | null {
    if (!state.startDateTime) {
      this.state.controls.startDateTime.setErrors({ matDatepickerParse: true });
      return null;
    }

    if (!state.endDateTime) {
      this.state.controls.endDateTime.setErrors({ matDatepickerParse: true });
      return null;
    }

    if (state.startDateTime >= state.endDateTime) {
      this.state.controls.startDateTime.setErrors({
        ...this.state.controls.startDateTime.errors,
        matStartDateInvalid: true,
      });
      this.state.controls.endDateTime.setErrors({
        ...this.state.controls.endDateTime.errors,
        matEndDateInvalid: true,
      });
    }

    if (this.state.controls.startDateTime.errors || this.state.controls.endDateTime.errors) {
      return null;
    }

    return JSON.stringify({
      option: state.selectedOption,
      value: { start: state.startDateTime.toISOString(), end: state.endDateTime.toISOString() },
    });
  }
}
